import React from "react";
import { Box, Skeleton, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useFormik } from "formik";
import * as Yup from "yup";

import { useServices, useSnackbar } from "contexts";
import { getUserById, updateUserProfileById } from "services";
import { Session } from "utils";

import { fieldSx, FieldLabel, ErrorText } from "../../components/FormFields";

/**
 * Name + email form — the mobile port of `components/Profile/PersonalDetails`.
 *
 * Same platform calls as desktop (`getUserById` on mount, `updateUserProfileById`
 * on save) and the same payload: only the name is editable, the email is
 * read-only, and the `applying*` metadata is round-tripped untouched so saving
 * the name never clears it.
 */

const Schema = new Yup.ObjectSchema({
  name: Yup.string().required("Please fill your name"),
  email: Yup.string().email().required("Please fill your email address"),
  applyingJobRole: Yup.string(),
  applyingPosition: Yup.string(),
  applyingCompany: Yup.string(),
});

export default function PersonalDetails() {
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = React.useState(false);
  const [fetching, setFetching] = React.useState(true);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      applyingJobRole: "",
      applyingPosition: "",
      applyingCompany: "",
    },
    validationSchema: Schema,
    onSubmit: async (values) => {
      try {
        setLoading(true);

        await service.callService(updateUserProfileById, {
          name: values.name,
          metadata: {
            applyingJobRole: values.applyingJobRole,
            applyingPosition: values.applyingPosition,
            applyingCompany: values.applyingCompany,
          },
        });

        snackbar.success("Saved profile successfully!");
      } catch (error) {
        snackbar.error("Uh Oh! Unable to update your profile");
      } finally {
        setLoading(false);
      }
    },
  });

  const { setValues } = formik;

  React.useEffect(() => {
    (async () => {
      try {
        const result = await service.callService(getUserById, Session.userId);

        setValues({
          name: result?.name || "",
          email: result?.email || "",
          applyingJobRole: result?.metadata?.applyingJobRole || "",
          applyingPosition: result?.metadata?.applyingPosition || "",
          applyingCompany: result?.metadata?.applyingCompany || "",
        });
      } catch (error) {
        snackbar.error("Uh Oh! Unable to load profile");
      } finally {
        setFetching(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nameError =
    formik.touched.name && !!formik.errors.name ? formik.errors.name : "";

  if (fetching) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <Skeleton variant="rectangular" height={68} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={68} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      noValidate
      sx={{ display: "flex", flexDirection: "column", gap: 5 }}
    >
      <Box>
        <FieldLabel>Full Name</FieldLabel>
        <TextField
          id="name"
          name="name"
          placeholder="Your name"
          fullWidth
          size="small"
          value={formik.values.name || ""}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          sx={(theme) => fieldSx(theme, !!nameError)}
        />
        {nameError && <ErrorText>{nameError}</ErrorText>}
      </Box>

      <Box>
        <FieldLabel>Email</FieldLabel>
        <TextField
          id="email"
          name="email"
          fullWidth
          size="small"
          disabled
          value={formik.values.email || ""}
          sx={(theme) => fieldSx(theme, false)}
        />
      </Box>

      <LoadingButton type="submit" variant="contained" loading={loading} fullWidth>
        Save Details
      </LoadingButton>
    </Box>
  );
}
